import React, { useRef } from 'react';
import dynamic from 'next/dynamic';
import { ColorKeys } from '@/styles/colors';
import beTwMerge from '@/utils/beTwMerge';
import useMediaQuery from '@/hooks/useMediaQuery';
import ResponsiveImage, { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import RichText from '@/components/RichText/RichText';
import { VideoPlayerHandle } from '@/components/HeroBanner/HeroVideoPlayer';

const HeroVideoPlayerDynamic = dynamic(() => import('@/components/HeroBanner/HeroVideoPlayer'));

export type ResponsiveSet = {
  desktop?: string;
  tablet?: string;
  mobile?: string;
};

export type Action = {
  label: string;
  variant?: 'primary' | 'secondary';
  color?: ColorKeys;
  mobileVariant?: 'primary' | 'secondary';
  mobileColor?: ColorKeys;
  onClick?: () => void;
  href?: string;
  target?: string;
  actions?: Action[];
};

export type HeroContentVerticalPosition = 'top' | 'center' | 'bottom' | '';
export type HeroContentHorizontalPosition = 'left' | 'center' | 'right' | '';
export type HeroVariant = 'large' | 'standard';

export type HeroBannerProps = {
  title?: string | React.ReactNode;
  description?: string;
  textColor: 'white' | 'black';
  background?: ResponsiveImageSet;
  backgroundMobile?: ResponsiveImageSet;
  fileVideoSrc?: string;
  hasIcon?: boolean;
  iconFill?: string;
  isHeader?: boolean;
  fileMobileVideoSrc?: string;
  loop?: boolean;
  controls?: boolean;
  autoPlay?: boolean;
  // sectionLink?: string;
  imgAlt?: string;
  className?: string;
};

export default function HeroBanner({
  title,
  description,
  hasIcon,
  iconFill = '#305D5D',
  isHeader = false,
  textColor,
  background,
  backgroundMobile,
  loop = true,
  controls = true,
  autoPlay = true,
  fileVideoSrc,
  fileMobileVideoSrc,
  // sectionLink,
  imgAlt,
  className,
}: HeroBannerProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const HeaderTag = isHeader ? 'h1' : 'h2';
  const titleString = typeof title === 'string' ? title : '';

  const mobileBackground = Array.isArray(backgroundMobile)
    ? backgroundMobile.find(a => a.breakpoint <= 1023)?.src
    : undefined;
  const desktopBackground = Array.isArray(background) ? background.find(a => a.breakpoint >= 1024)?.src : undefined;

  const videoPlayerRef = useRef<VideoPlayerHandle>(null);

  const videoOptions = {
    autoplay: autoPlay,
    controls,
    loop,
    muted: true,
    hasSound: false,
    variant: 'mission',
    position: 'left',
  };

  // eslint-disable-next-line no-nested-ternary
  const media = fileVideoSrc ? (
    <HeroVideoPlayerDynamic
      ref={videoPlayerRef}
      videoSrc={isDesktop ? fileVideoSrc : fileMobileVideoSrc}
      poster={isDesktop ? desktopBackground : mobileBackground}
      options={videoOptions}
      onVideoReady={() => null}
      className="tw-object-cover tw-w-full tw-h-full"
    />
  ) : (
    <div className="tw-w-full tw-inline-block tw-overflow-hidden tw-h-full tw-relative">
      <ResponsiveImage
        loading="eager"
        className="tw-w-full tw-h-full tw-object-cover tw-hidden lg:tw-block"
        sources={background}
        alt={imgAlt || titleString}
      />
      <ResponsiveImage
        loading="eager"
        className="tw-w-full tw-h-full tw-object-cover tw-block lg:tw-hidden"
        sources={backgroundMobile}
        alt={imgAlt || titleString}
      />
    </div>
  );

  return (
    <div className="tw-w-full">
      <div
        className={beTwMerge(
          `tw-bg-color-grey-100 tw-mx-auto tw-relative min-[1440px]:tw-w-full min-[1440px]:tw-h-full tw-flex`,
          className
        )}
      >
        <div
          className={beTwMerge(
            'tw-w-full tw-inline-block tw-overflow-hidden tw-h-full tw-relative tw-aspect-[390/400] md:tw-aspect-[768/788] lg:tw-aspect-[1440/400]'
          )}
        >
          {media}
        </div>

        <div
          className={beTwMerge(`tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-right-0 tw-flex tw-pointer-events-none`)}
        >
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full tw-gap-y-4">
            {hasIcon && (
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path
                  d="M23.9996 37.4198C23.398 35.2892 19.9017 23.6982 9.40576 18.6663C20.0504 13.4816 23.9996 0 23.9996 0C23.9996 0 27.9489 13.4816 38.5935 18.6663C28.0975 23.6982 24.6012 35.2819 23.9996 37.4198Z"
                  fill={iconFill}
                />
                <path
                  d="M0 25.8145C4.62163 42.1102 21.82 48.0002 21.82 48.0002C21.82 48.0002 13.2279 25.8145 0 25.8145Z"
                  fill={iconFill}
                />
                <path
                  d="M47.9997 25.8145C34.5736 25.8145 26.1797 48.0002 26.1797 48.0002C26.1797 48.0002 43.3781 42.1102 47.9997 25.8145Z"
                  fill={iconFill}
                />
              </svg>
            )}
            {title && (
              <HeaderTag
                className={beTwMerge(
                  'tw-mb-0 tw-mt-2 max-md:tw-text-[32px] tw-text-center',
                  textColor === 'white' ? 'tw-text-white' : 'tw-text-color-black'
                )}
              >
                {typeof title === 'string' ? title : title}
              </HeaderTag>
            )}
            {description && (
              <RichText
                className={beTwMerge(
                  'tw-max-w-[324px] lg:tw-max-w-[476px] tw-m-0 tw-font-avenirmedium tw-text-inherit tw-text-center max-lg:tw-text-be-base lg:tw-text-be-lg',
                  textColor === 'white' ? 'tw-text-white' : 'tw-text-color-black'
                )}
                content={description}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
